<template>
    <div class="animated fade-in">

        <c-data-table
                class="my-4"
                :items="items"
                :fields="fields"
                :table-filter="true"
                :items-per-page="params.limit"
                items-per-page-select
                hover
                sorter
                :pagination="false"
                :sorterValue="params.sort"
                :tableFilterValue="params.filter"
                :loading="loading"
                @pagination-change="pageChange"
                @update:sorter-value="sortChange"
                @update:table-filter-value="filterChange">

            <template #number="{item}">
                <td>
                    <router-link v-if="item.number" :to="'/estimates/generated/'+item.id+'/edit'">{{ item.number }}</router-link>
                </td>
            </template>
            <template #customer_name="{item}">
                <td>{{ item.customer_name ? item.customer_name : null }}</td>
            </template>
            <template #plan_number="{item}">
                <td>{{ item.plan_number ? item.plan_number : null }}</td>
            </template>
            <template #contact="{item}">
                <td>
                    <span v-if="item.contact">{{ item.contact.full_name }}</span>
                </td>
            </template>
            <template #address="{item}">
                <td>
                    <span v-if="item.shipping">{{ item.shipping.address }}</span>
                </td>
            </template>
            <template #count="{item}">
                <td>
                    {{ countItems(item) }}
                </td>
            </template>
            <template #total="{item}">
                <td>
                    {{ total ? '$' + Math.abs(item.total).toFixed(2) : null }}
                </td>
            </template>
            <template #actions="{item, index}">
                <td>
                    <b-button size="sm" class="px-1 py-0 mr-2" @click.prevent="linkModal(item, index)" v-b-tooltip.hover title="Share Link"><i class="fal fa-link"></i></b-button>
                    <b-button variant="info" size="sm" class="px-1 py-0 mr-2" :to="'/estimates/generated/'+item.id+'/edit'"><i class="fal fa-pencil"></i></b-button>
                    <b-button variant="danger" size="sm" class="px-1 py-0" @click.prevent="deleteEstimate(item, index)"><i class="fal fa-trash"></i></b-button>
                </td>
            </template>

        </c-data-table>

        <b-modal ref="share-link" size="lg" :ok-only="true" :title="'Share'+(share_estimate ? ' #'+share_estimate.number : '')">
          <b-overlay v-if="!share_link" :show="true">
            <div class="py-4"></div>
          </b-overlay>
          <b-button-group v-else>
            <b-button @click.prevent="copyShare">{{share_link}}</b-button>
            <b-button variant="info" @click.prevent="copyShare"><i class="fal fa-clipboard"></i></b-button>
            <b-button variant="info" :href="share_link" target="_blank"><i class="fal fa-external-link"></i></b-button>
          </b-button-group>
          <input style="position:absolute;left:-99999px;" ref="share-link-val" :value="share_link">
        </b-modal>

        <c-pagination
                :active-page="params.page"
                :pages="Math.ceil(+(total/params.limit))"
                @update:activePage="updatePageNum"
        ></c-pagination>

    </div>
</template>

<script>
  import Contact from "@/models/Contact";
  import Location from "@/models/Location";
  import CustomerEstimateItem from "@/models/CustomerEstimateItem";
  import CustomerEstimate from "@/models/CustomerEstimate";
  import ToastNotifications from "@/components/shared/mixins/ToastNotifications";
  import {FETCH_ENTITY, GREEDY_FETCH_ENTITY} from "@/store/action-types";

  export default {
    name: 'CustomerEstimateTable',
    components: {},
    mixins: [ToastNotifications],
    data () {
      return {
        loading: true,
        estimates: [],
        items: [],
        fields: [
          { key: 'number', label: '#' },
          { key: 'customer_name', label: 'Customer', sorter: false },
          { key: 'plan_number', label: 'Plan', sorter: false },
          { key: 'contact', label: 'Contact', sorter: false },
          { key: 'address', label: 'Address', sorter: false },
          { key: 'count', label: 'Count', sorter: false },
          { key: 'total', label: 'Total', sorter: false },
          { key: 'actions', label: 'Actions', sorter: false },
        ],
        total: null,
        params: {
          page: 1,
          limit: 20,
          sort: { column: 'index', asc : false},
          filter: null
        },
        share_estimate: null,
        share_link: null,
      }
    },
    methods: {
      countItems: function(estimate) {
        let count = 0;
        estimate.customer_estimate_items.forEach((item) => {
          count = count + +(item.quantity);
        });
        return count;
      },
      cleanString: function (html) {
        let txt = document.createElement('textarea');
        txt.innerHTML = html;
        return decodeURI(unescape(txt.value));
      },
      pageChange: function(val) {
        this.params.limit = val;
        this.fetchEstimates();
      },
      sortChange: function(val) {
        this.params.sort.column = val.column;
        this.params.sort.asc = val.asc;
        this.fetchEstimates();
      },
      filterChange: function(val) {
        this.params.filter = (val ? (val != '' ? val : null) : null);
        this.fetchEstimates();
      },
      updatePageNum: function(val) {
        this.params.page = val;
        this.fetchEstimates();
      },
      fetchEstimates: function() {
        this.loading = true;
        let params = JSON.parse(JSON.stringify(this.params));
        if(params && params.filter) {
          params.number = params.filter;
        }
        delete params.filter;
        this.$store.dispatch(FETCH_ENTITY, {model: CustomerEstimate, params}).then((results) => {
          this.total = results.pagination.total || 0;
          if(!this.total) {
            this.estimates = [];
            this.items = [];
            this.loading = false;
            return;
          }
          this.estimates = results.entities[CustomerEstimate.entity];
          this.estimates = this.estimates ? this.estimates : [];
          let identifiers = [];
          let contact_ids = [];
          let location_ids = [];
          this.estimates.forEach((estimate) => {
            identifiers.push(estimate.id);
            contact_ids.push(estimate.contact_id);
            location_ids.push(estimate.shipping_id);
          });
          Promise.all([
            this.$store.dispatch(GREEDY_FETCH_ENTITY, {model: Contact, params: {id: contact_ids.join('|')}}),
            this.$store.dispatch(GREEDY_FETCH_ENTITY, {model: Location, params: {id: location_ids.join('|')}}),
            this.$store.dispatch(GREEDY_FETCH_ENTITY, {model: CustomerEstimateItem, params: {customer_estimate_id: identifiers.join('|')}}),
          ]).then(() => {
            let estimates = [];
            this.estimates.forEach((estimate) => {
              let temp = CustomerEstimate.query().where('id', estimate.id).with([
                'contact',
                'shipping',
                'customer_estimate_items'
              ]).first();
              estimates.push(temp);
            });
            this.estimates = estimates;
            this.items = estimates;
            this.loading = false;
          });
        });
      },
      deleteEstimate(estimate) {
        CustomerEstimate.api().remove(estimate.id).then((results) => {
          this.quickSuccessToast('Estimate #'+estimate.number+' Deleted', 'The estimate was deleted successfully.');
          CustomerEstimate.delete(estimate.id);
          let index = this.items.findIndex(i=>+(i.id)===+(estimate.id));
          this.items.splice(index, 1);
        },() => {
          this.quickWarningToast('Delete Failed', 'The estimate could not be deleted, please try again.');
        });
      },
      linkModal(estimate, index) {
        this.share_estimate = estimate;
        this.share_link = null;
        CustomerEstimate.api().share(this.share_estimate.id).then((results) => {
          this.share_link = results.response.data;
        });
        this.$refs['share-link'].show();
      },
      copyShare() {
        var copyText = this.$refs['share-link-val'];
        copyText.select();
        copyText.setSelectionRange(0, 99999);
        document.execCommand("copy");
        this.quickSuccessToast('Link Copied','The estimate pdf link has been copied to your clipboard.');
      }
    },
    created() {
      this.fetchEstimates();
    }
  }
</script>
